import {proxy, ref} from "valtio";

import {CouponStatus} from "@/entities/booking/types";

import {EventEmitter} from "@/shared/event-emitter";
import {Field, type FieldInstance} from "@/shared/models/form/fields";

export type {CouponStatus} from "@/entities/booking/types";

export interface CouponEvents {
    apply: (code: string) => void;
    clear: () => void;
}

export interface CouponError {
    /** Machine-readable identifier, used to trigger setError() by name */
    type: string;
    /** i18n translation key shown in CouponView */
    messageKey: string;
}

export interface CouponConfig {
    initialCode?: string;
    /** Additional or override errors. Merged with default errors (custom keys take precedence). */
    errors?: Record<string, CouponError>;
}

/** Default errors available in every coupon instance. Can be extended via config.errors. */
export const DEFAULT_COUPON_ERRORS: Record<string, CouponError> = {
    promoCodeInvalid: {type: "promo_code_invalid", messageKey: "promoCodeInvalid"},
};

class CouponClass {
    readonly code: FieldInstance;

    status: CouponStatus = CouponStatus.Idle;
    error: CouponError | null = null;
    _disabled: boolean = false;

    readonly events: EventEmitter<CouponEvents>;

    private readonly errorMap: Record<string, CouponError>;

    constructor(config?: CouponConfig) {
        this.code = Field({initialValue: config?.initialCode ?? ""});
        this.events = ref(new EventEmitter<CouponEvents>()) as EventEmitter<CouponEvents>;
        this.errorMap = {...DEFAULT_COUPON_ERRORS, ...config?.errors};
        if (config?.initialCode) {
            this.status = CouponStatus.Applied;
        }
    }

    get codeValue(): string {
        return this.code.value.trim();
    }

    getValue(): string | undefined {
        const value = this.codeValue;
        return value || undefined;
    }

    /** Translation key for current error (for CouponView rendering) */
    get errorKey(): string | null {
        return this.error?.messageKey ?? null;
    }

    get isApplying(): boolean {
        return this.status === CouponStatus.Applying;
    }

    get isApplied(): boolean {
        return this.status === CouponStatus.Applied;
    }

    get hasError(): boolean {
        return this.status === CouponStatus.Error && this.error?.type !== "no_dates";
    }

    get hasInfo(): boolean {
        return this.status === CouponStatus.Error && this.error?.type === "no_dates";
    }

    apply(): void {
        const value = this.codeValue;
        if (!value) {
            return;
        }
        this.status = CouponStatus.Applying;
        this.error = null;
        this.events.emit("apply", value);
    }

    setApplied(): void {
        this.status = CouponStatus.Applied;
        this.error = null;
    }

    /**
     * Set error by type key (from config.errors) or by raw messageKey string.
     *
     * @example
     * // Named error from config
     * coupon.setError("noDates");
     *
     * // Raw translation key (fallback, no type)
     * coupon.setError("promoCodeInvalid");
     */
    setError(typeOrMessageKey: string): void {
        this.status = CouponStatus.Error;
        this.error = this.errorMap[typeOrMessageKey] ?? {type: typeOrMessageKey, messageKey: typeOrMessageKey};
    }

    get isDisabled(): boolean {
        return this.code.disabled;
    }

    setDisabled(value: boolean): void {
        if (value) {
            this.code.disable();
        } else {
            this.code.enable();
        }
    }

    resetStatus(): void {
        this.status = CouponStatus.Idle;
        this.error = null;
    }

    clear(): void {
        this.code.clear();
        this.status = CouponStatus.Idle;
        this.error = null;
        this.events.emit("clear");
    }
}

export type CouponInstance = CouponClass;

export function Coupon(config?: CouponConfig): CouponInstance {
    return proxy(new CouponClass(config));
}
